// Wizard state machine. The wizard is a five-step flow:
//
//   0. Months   — pick from inventory (rollup-eligible only)
//   1. Filter   — Python-style filter expression
//   2. Search   — optional Lucene post-filter
//   3. Output   — S3 prefix (auto-suggested when default bucket configured)
//   4. Review   — count estimate + submit
//
// State lives in a useReducer; the reducer mirrors changes to URL search
// params so a refresh / share-link / back-button preserves the in-progress
// config without smearing across React re-mounts.

export type StepIndex = 0 | 1 | 2 | 3 | 4;

// Available output formats. Names match what the Python CLI accepts
// (paim.cli._export.EXPORT_FORMATS) — the operator's shim translates
// these into the C# binary's internal vocabulary. ``condensed-json``
// is what unlocks the search-env feature (Phase 2 of search-envs).
export type OutputFormat = "original" | "ndjson" | "condensed-json";

export const OUTPUT_FORMATS: OutputFormat[] = [
  "original",
  "ndjson",
  "condensed-json",
];

// Legacy format names accepted on read (saved configs, clone URLs,
// pre-2.0 workflows) → the canonical "original". "original" replaced
// "xml" once podcast (JSON) documents joined broadcast (XML): the format
// emits each source document verbatim, so a corpus-specific name was
// wrong. See src/paim/cli/_export.py for the matching server-side map.
const LEGACY_FORMAT_ALIASES: Record<string, OutputFormat> = {
  xml: "original",
  "xml-zip": "original",
  zip: "original",
};

// The corpora a wizard run can target. A run may span more than one —
// counts union across them and the export fans out one Job per
// (content type, month) that actually has a rollup.
export const CONTENT_TYPES = ["broadcast", "podcast"] as const;
export type ContentType = (typeof CONTENT_TYPES)[number];

export function isContentType(s: string): s is ContentType {
  return (CONTENT_TYPES as readonly string[]).includes(s);
}

export interface ExportWizardState {
  step: StepIndex;
  // Which corpora to draw from. Multi-select: [] is invalid (step-0
  // gate blocks it), one entry is a single-corpus export, both is the
  // mixed case. Kept sorted in CONTENT_TYPES order for stable URLs.
  contentTypes: string[];
  months: string[]; // sorted "YYYY-MM" strings
  filter: string;
  search: string;
  output: string;
  outputFormat: OutputFormat;
  // One-or-two-sentence dataset description the user enters on the
  // Review step. Pre-populated from the AI summariser when the portal
  // has OpenAI tokens; otherwise empty and free-form. Persisted as
  // ``description`` inside params_json on the workflow row.
  description: string;
  // Append a report stage to the workflow, which aggregates the export's
  // demographic sidecars into a report/v1 document at the output root once
  // every export job has succeeded.
  //
  // Opt-in, and defaulted off deliberately: the stage costs a pod, and a
  // great many exports feed a pipeline nobody will read a report about.
  // Making it a deliberate choice keeps the cost attached to the intent.
  generateReport: boolean;
}

export const initialWizardState: ExportWizardState = {
  step: 0,
  contentTypes: ["broadcast"],
  months: [],
  filter: "",
  search: "",
  output: "",
  outputFormat: "original",
  description: "",
  generateReport: false,
};

export type WizardAction =
  | { type: "GO_TO_STEP"; step: StepIndex }
  | { type: "TOGGLE_CONTENT_TYPE"; contentType: string }
  | { type: "TOGGLE_MONTH"; month: string }
  | { type: "SET_MONTHS"; months: string[] }
  | { type: "SET_FILTER"; filter: string }
  | { type: "SET_SEARCH"; search: string }
  | { type: "SET_OUTPUT"; output: string }
  | { type: "SET_OUTPUT_FORMAT"; outputFormat: OutputFormat }
  | { type: "SET_GENERATE_REPORT"; generateReport: boolean }
  | { type: "SET_DESCRIPTION"; description: string }
  | { type: "HYDRATE"; state: ExportWizardState };

export function wizardReducer(
  state: ExportWizardState,
  action: WizardAction,
): ExportWizardState {
  switch (action.type) {
    case "GO_TO_STEP":
      return { ...state, step: action.step };
    case "TOGGLE_CONTENT_TYPE": {
      // Toggle one corpus in/out, kept in CONTENT_TYPES order. Month
      // coverage is per corpus, so reset the selection whenever the set
      // changes — the eligible months differ once the corpora do.
      const has = state.contentTypes.includes(action.contentType);
      const next = has
        ? state.contentTypes.filter((c) => c !== action.contentType)
        : CONTENT_TYPES.filter(
            (c) => state.contentTypes.includes(c) || c === action.contentType,
          );
      return { ...state, contentTypes: next, months: [] };
    }
    case "TOGGLE_MONTH": {
      const exists = state.months.includes(action.month);
      const next = exists
        ? state.months.filter((m) => m !== action.month)
        : [...state.months, action.month].sort();
      return { ...state, months: next };
    }
    case "SET_MONTHS":
      return { ...state, months: [...action.months].sort() };
    case "SET_FILTER":
      return { ...state, filter: action.filter };
    case "SET_SEARCH":
      return { ...state, search: action.search };
    case "SET_OUTPUT":
      return { ...state, output: action.output };
    case "SET_OUTPUT_FORMAT":
      return { ...state, outputFormat: action.outputFormat };
    case "SET_GENERATE_REPORT":
      return { ...state, generateReport: action.generateReport };
    case "SET_DESCRIPTION":
      return { ...state, description: action.description };
    case "HYDRATE":
      return action.state;
  }
}

// --- step gating ---

export function canAdvance(state: ExportWizardState, step: StepIndex): boolean {
  switch (step) {
    case 0:
      return state.contentTypes.length > 0 && state.months.length > 0;
    case 1:
      // Filter is optional. Empty filter ⇒ every program in the
      // selected months — large + slow + costly. The submit step
      // surfaces a confirmation modal when the estimate crosses
      // a doc-count threshold, so the user gets one explicit
      // "I understand" click before kicking off a giant export.
      return true;
    case 2:
      return true; // search is optional
    case 3:
      return state.output.trim().length > 0 && state.output.startsWith("s3://");
    case 4:
      return false; // last step submits
  }
}

// --- URL sync ---

// Encode the wizard's free-form fields as URL search params so refresh
// / share-link works. We don't try to encode the `step` itself — that's
// state we want to reset on refresh so the user re-confirms each step.
export function stateToSearchParams(state: ExportWizardState): URLSearchParams {
  const p = new URLSearchParams();
  // Persist unless it's the lone default. "broadcast" alone is the
  // default, so leave it out to keep clean URLs; anything else (podcast,
  // or a multi-corpus set) is encoded as a CSV.
  const isDefaultCorpus =
    state.contentTypes.length === 1 && state.contentTypes[0] === "broadcast";
  if (state.contentTypes.length && !isDefaultCorpus) {
    p.set("content_types", state.contentTypes.join(","));
  }
  if (state.months.length) p.set("months", state.months.join(","));
  if (state.filter) p.set("filter", state.filter);
  if (state.search) p.set("search", state.search);
  if (state.output) p.set("output", state.output);
  if (state.outputFormat !== "original")
    p.set("output_format", state.outputFormat);
  if (state.description) p.set("description", state.description);
  // Only serialised when on, so a shared URL without it means "no report"
  // rather than "unspecified" — the same convention output_format uses for
  // its default.
  if (state.generateReport) p.set("generate_report", "1");
  return p;
}

export function searchParamsToState(
  qs: URLSearchParams,
): Omit<ExportWizardState, "step"> {
  const monthsRaw = qs.get("months") || "";
  const months = monthsRaw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => /^\d{4}-\d{2}$/.test(s))
    .sort();
  const fmtRaw = qs.get("output_format") || "";
  const fmt = LEGACY_FORMAT_ALIASES[fmtRaw] ?? fmtRaw;
  const outputFormat: OutputFormat = OUTPUT_FORMATS.includes(
    fmt as OutputFormat,
  )
    ? (fmt as OutputFormat)
    : "original";
  // Parse content_types CSV → validated, de-duped, CONTENT_TYPES order.
  // Falls back to the broadcast default when absent or all-invalid.
  const ctRaw = (qs.get("content_types") || "")
    .split(",")
    .map((s) => s.trim())
    .filter(isContentType);
  const contentTypes = CONTENT_TYPES.filter((c) => ctRaw.includes(c));
  return {
    contentTypes: contentTypes.length ? contentTypes : ["broadcast"],
    months,
    filter: qs.get("filter") || "",
    search: qs.get("search") || "",
    output: qs.get("output") || "",
    outputFormat,
    description: qs.get("description") || "",
    generateReport: qs.get("generate_report") === "1",
  };
}

// --- output prefix auto-suggestion ---

// Build a sensible default prefix:
//   s3://<bucket>/<user>/<YYYY-MM-DD>-<short-id>/
// The short-id keeps two exports the same user ran on the same day
// from clobbering each other. The user can edit before submitting.
export function suggestOutputPrefix(args: {
  defaultBucket: string | null;
  userEmail: string | null;
  date?: Date;
}): string {
  const { defaultBucket, userEmail } = args;
  if (!defaultBucket) return "";
  const date = args.date ?? new Date();
  const stamp = date.toISOString().slice(0, 10); // YYYY-MM-DD
  const shortId = Math.random().toString(36).slice(2, 8);
  // Sanitize user — emails contain @ and . which aren't great in s3
  // path components for some tools.
  const userSeg = (userEmail ?? "anon").replace(/[^a-zA-Z0-9_.-]/g, "_");
  return `s3://${defaultBucket}/${userSeg}/${stamp}-${shortId}/`;
}
